import {
  Card, Suit, SeatIndex, TeamId, TEAM_OF, BidValue, Bid, Trick, HandState,
  GameState, HandScore, GameEvent, Ruleset, DEFAULT_RULESET, Rank,
} from "../types/game";
import {
  newDeck, shuffle, makeRng, dealHands, nextSeat, cardEquals, GAME_POINTS
} from "./deck";

// ============================================================
// Starting a hand
// ============================================================

export function startHand(
  handNumber: number,
  dealer: SeatIndex,
  seed: number
): HandState {
  // For single-player convenience we inline the undealt 28 onto the hand so
  // coke redraws don't need a separate state channel. Multiplayer builds
  // states server-side and never writes this field into the public doc.
  const { state, deck } = startHandWithDeck(handNumber, dealer, seed);
  return { ...state, deckRemaining: deck.slice(24) };
}

// Like startHand but also returns the full shuffled deck. The first 24 cards
// are the dealt hands; the remaining 28 are saved as "undealt" for coke-hand
// redraws under the vacation ruleset. Callers that don't need the deck can
// use startHand(); callers that run the vacation ruleset (local quick-play
// and the multiplayer server) should keep the deck alongside the state.
export function startHandWithDeck(
  handNumber: number,
  dealer: SeatIndex,
  seed: number,
): { state: HandState; deck: Card[] } {
  const rng = makeRng(seed);
  const deck = shuffle(newDeck(), rng);
  const hands = dealHands(deck, dealer);

  const state: HandState = {
    handNumber,
    dealer,
    hands,
    bids: [],
    tricks: [],
    phase: "bidding",
    turn: nextSeat(dealer),
    capturedCards: { A: [], B: [] },
    eventLog: [{ type: "deal", seed, dealer }],
    cokeClaimed: [],
  };
  return { state, deck };
}

// ============================================================
// Coke hand (vacation ruleset)
// ============================================================

// A "coke hand" is an initial 6-card hand whose ranks are all in 3..9 — no 2
// (low), no 10 (game points), and no face cards or ace. In vacation rules the
// holder may forfeit the right to bid this hand in exchange for 6 fresh cards.
const COKE_MIN: Rank = 3;
const COKE_MAX: Rank = 9;

export function isCokeHand(cards: Card[]): boolean {
  if (cards.length !== 6) return false;
  for (const c of cards) {
    if (c.rank < COKE_MIN || c.rank > COKE_MAX) return false;
  }
  return true;
}

export function canClaimCoke(
  state: HandState,
  seat: SeatIndex,
  ruleset: Ruleset,
): boolean {
  if (ruleset !== "vacation") return false;
  if (state.phase !== "bidding") return false;
  if (state.turn !== seat) return false;
  if ((state.cokeClaimed ?? []).includes(seat)) return false;
  return isCokeHand(state.hands[seat]);
}

// Single-player convenience: draws 6 cards from state.deckRemaining and
// forwards to applyClaimCoke. Use this on the client; the multiplayer server
// keeps the deck in a private doc and supplies newCards explicitly.
export function applyClaimCokeLocal(
  state: HandState,
  seat: SeatIndex,
  ruleset: Ruleset,
): HandState {
  const pile = state.deckRemaining ?? [];
  if (pile.length < 6) {
    throw new Error("Not enough undealt cards for a coke redraw");
  }
  const newCards = pile.slice(0, 6);
  const remaining = pile.slice(6);
  const withNewDeck: HandState = { ...state, deckRemaining: remaining };
  return applyClaimCoke(withNewDeck, seat, newCards, ruleset);
}

// Caller supplies `newCards` (six cards drawn off the top of the saved deck's
// undealt pile). Engine swaps them in, marks the seat as coke-claimed, and
// applies an automatic pass bid — a coke-claimed seat is forbidden from
// bidding this hand.
export function applyClaimCoke(
  state: HandState,
  seat: SeatIndex,
  newCards: Card[],
  ruleset: Ruleset,
): HandState {
  if (!canClaimCoke(state, seat, ruleset)) {
    throw new Error("Cannot claim coke from this position");
  }
  if (newCards.length !== 6) {
    throw new Error("applyClaimCoke requires exactly 6 replacement cards");
  }
  const newHands = state.hands.map((h, i) => (i === seat ? newCards : h));
  const cokeClaimed = [...(state.cokeClaimed ?? []), seat];
  const withCoke: HandState = {
    ...state,
    hands: newHands,
    cokeClaimed,
    eventLog: [...state.eventLog, { type: "cokeClaim", seat }],
  };
  // Coke claim forces a pass from this seat. Goes through applyBid so the
  // stuck-dealer/abandon bookkeeping lives in one place.
  return applyBid(withCoke, { seat, value: "pass" }, ruleset);
}

// ============================================================
// Bidding
// ============================================================

export function legalBids(state: HandState, seat?: SeatIndex): (BidValue | "pass")[] {
  if (state.phase !== "bidding") return [];
  // Coke-claimed seats can only pass; no real bid is legal for them.
  const bidder = seat ?? state.turn;
  if (bidder !== undefined && (state.cokeClaimed ?? []).includes(bidder)) {
    return ["pass"];
  }
  const highest = currentHighBid(state.bids);
  const options: (BidValue | "pass")[] = ["pass"];
  for (const v of [2, 3, 4] as BidValue[]) {
    if (v > highest) options.push(v);
  }
  return options;
}

function currentHighBid(bids: Bid[]): number {
  let high = 1; // min legal bid is 2
  for (const b of bids) {
    if (b.value !== "pass" && b.value > high) high = b.value;
  }
  return high;
}

export function applyBid(
  state: HandState,
  bid: Bid,
  ruleset: Ruleset = DEFAULT_RULESET,
): HandState {
  if (state.phase !== "bidding") throw new Error("Not in bidding phase");
  if (state.turn !== bid.seat) throw new Error("Not this seat's turn to bid");
  if (!legalBids(state, bid.seat).includes(bid.value)) throw new Error("Illegal bid");

  const bids = [...state.bids, bid];
  const eventLog = [...state.eventLog, { type: "bid", seat: bid.seat, value: bid.value } as GameEvent];

  // Has everyone bid? Bidding goes once around the table (4 bids total).
  if (bids.length === 4) {
    return resolveBidding({ ...state, bids, eventLog });
  }

  // Stuck-dealer short-circuit: if 3 non-dealers have all passed in TPC, the
  // dealer is auto-forced into a 2 bid without needing to click. Vacation
  // always waits for the dealer's explicit turn so they can claim coke if
  // eligible — one extra click is worth the uniform coke mechanic.
  if (
    bids.length === 3 &&
    bids.every(b => b.value === "pass") &&
    ruleset !== "vacation"
  ) {
    return resolveBidding({ ...state, bids, eventLog });
  }

  return {
    ...state,
    bids,
    eventLog,
    turn: nextSeat(bid.seat),
  };
}

function resolveBidding(state: HandState): HandState {
  const nonPassBids = state.bids.filter(b => b.value !== "pass");
  const cokeClaimed = state.cokeClaimed ?? [];

  if (nonPassBids.length === 0) {
    // If the dealer claimed coke, they can't be forced into a 2 bid. Nobody
    // can bid → hand is abandoned, score is zero, deal the next hand.
    if (cokeClaimed.includes(state.dealer)) {
      return {
        ...state,
        phase: "done",
        abandoned: true,
        score: { teamA: 0, teamB: 0, bidderMade: false, gamePointsA: 0, gamePointsB: 0 },
        eventLog: [...state.eventLog, { type: "abandoned" }],
      };
    }
    return {
      ...state,
      bidder: state.dealer,
      bid: 2,
      phase: "choosingTrump",
      turn: state.dealer,
      eventLog: [...state.eventLog, { type: "forcedBid", seat: state.dealer }],
    };
  }

  // Highest bid wins. If tied (shouldn't happen with our legal-bid rule), earliest wins.
  let winner = nonPassBids[0];
  for (const b of nonPassBids) {
    if (b.value !== "pass" && (winner.value === "pass" || b.value > winner.value)) {
      winner = b;
    }
  }

  return {
    ...state,
    bidder: winner.seat,
    bid: winner.value as BidValue,
    phase: "choosingTrump",
    turn: winner.seat,
  };
}

// ============================================================
// Choosing trump
// ============================================================

export function applyChooseTrump(state: HandState, seat: SeatIndex, trump: Suit): HandState {
  if (state.phase !== "choosingTrump") throw new Error("Not in choosingTrump phase");
  if (state.bidder !== seat) throw new Error("Only the high bidder picks trump");

  return {
    ...state,
    trump,
    phase: "playing",
    turn: state.bidder,
    currentTrick: { leader: state.bidder!, plays: [] },
    eventLog: [...state.eventLog, { type: "chooseTrump", seat, trump }],
  };
}

// ============================================================
// Playing cards
// ============================================================

// Computes which cards the given seat may legally play.
//
// Both TPC and Vacation use the same card-play rules: when following an
// off-suit led card, you may either follow suit OR throw any trump
// (discretionary trumping). If you're void in the led suit, you may play
// any card. The first lead of a hand must be trump. Trump-led tricks force
// a trump follow unless you're void in trump.
//
// The `ruleset` argument is kept for forward compatibility but currently
// doesn't branch — play mechanics are identical across our rulesets.
export function legalPlays(
  state: HandState,
  seat: SeatIndex,
  _ruleset: Ruleset = DEFAULT_RULESET,
): Card[] {
  if (state.phase !== "playing") return [];
  if (state.turn !== seat) return [];
  const hand = state.hands[seat];
  const trick = state.currentTrick!;
  const trump = state.trump!;

  // First lead of the hand MUST be trump.
  const isFirstLead = state.tricks.length === 0 && trick.plays.length === 0;
  if (isFirstLead) {
    const trumps = hand.filter(c => c.suit === trump);
    return trumps.length > 0 ? trumps : hand.slice();
  }

  // Leading a non-first trick: any card.
  if (trick.plays.length === 0) return hand.slice();

  const ledSuit = trick.plays[0].card.suit;

  // Trump led → must follow trump if able, otherwise any card.
  if (ledSuit === trump) {
    const trumps = hand.filter(c => c.suit === trump);
    return trumps.length > 0 ? trumps : hand.slice();
  }

  const inSuit = hand.filter(c => c.suit === ledSuit);
  const trumps = hand.filter(c => c.suit === trump);
  if (inSuit.length > 0) {
    // Follow suit OR throw any trump (discretionary trumping allowed).
    return [...inSuit, ...trumps];
  }
  // Void in led suit: any card.
  return hand.slice();
}

export function applyPlay(
  state: HandState,
  seat: SeatIndex,
  card: Card,
  ruleset: Ruleset = DEFAULT_RULESET,
): HandState {
  if (state.phase !== "playing") throw new Error("Not in playing phase");
  if (state.turn !== seat) throw new Error("Not this seat's turn");
  const legal = legalPlays(state, seat, ruleset);
  if (!legal.some(c => cardEquals(c, card))) throw new Error("Illegal play");

  const newHands = state.hands.map((h, i) =>
    i === seat ? h.filter(c => !cardEquals(c, card)) : h
  );
  const newTrick: Trick = {
    ...state.currentTrick!,
    plays: [...state.currentTrick!.plays, { seat, card }],
  };
  const eventLog = [...state.eventLog, { type: "play", seat, card } as GameEvent];

  // Trick complete?
  if (newTrick.plays.length === 4) {
    return resolveTrick({ ...state, hands: newHands, currentTrick: newTrick, eventLog });
  }

  return {
    ...state,
    hands: newHands,
    currentTrick: newTrick,
    turn: nextSeat(seat),
    eventLog,
  };
}

/**
 * Solo / local play only: true if the human has played in the current
 * incomplete trick and it is not yet their turn again ("your next card").
 * Rewinding removes their play and any plays after it (bots) in the same trick.
 */
export function canUndoSoloPlay(state: HandState, humanSeat: SeatIndex): boolean {
  if (state.phase !== "playing") return false;
  const t = state.currentTrick;
  if (!t || t.plays.length === 0 || t.plays.length >= 4) return false;
  const hi = t.plays.findIndex(p => p.seat === humanSeat);
  if (hi === -1) return false;
  // Once it's your turn to play again, undo is off — pick your next card.
  if (state.turn === humanSeat) return false;
  return true;
}

/**
 * Reverts the human's play in the current trick and every play after it
 * (same trick), restoring all affected hands. Turn goes back to the human.
 */
export function undoSoloPlay(state: HandState, humanSeat: SeatIndex): HandState {
  if (!canUndoSoloPlay(state, humanSeat)) {
    throw new Error("Cannot undo play");
  }
  const trick = state.currentTrick!;
  const hi = trick.plays.findIndex(p => p.seat === humanSeat);
  const removed = trick.plays.slice(hi);
  const newPlays = trick.plays.slice(0, hi);

  let hands = state.hands.map(h => [...h]);
  for (const { seat, card } of removed) {
    hands[seat] = [...hands[seat], card];
  }

  let eventLog = [...state.eventLog];
  for (let k = removed.length - 1; k >= 0; k--) {
    const { seat, card } = removed[k];
    const last = eventLog[eventLog.length - 1];
    if (
      last?.type === "play" &&
      last.seat === seat &&
      cardEquals(last.card, card)
    ) {
      eventLog = eventLog.slice(0, -1);
    }
  }

  return {
    ...state,
    hands,
    currentTrick: { ...trick, plays: newPlays },
    turn: humanSeat,
    eventLog,
  };
}

function resolveTrick(state: HandState): HandState {
  const trick = state.currentTrick!;
  const trump = state.trump!;
  const ledSuit = trick.plays[0].card.suit;

  let winningPlay = trick.plays[0];
  for (const p of trick.plays.slice(1)) {
    const cur = winningPlay.card;
    // Trump beats non-trump; higher trump beats lower trump; within led suit, highest wins.
    if (p.card.suit === trump && cur.suit !== trump) {
      winningPlay = p;
    } else if (p.card.suit === trump && cur.suit === trump && p.card.rank > cur.rank) {
      winningPlay = p;
    } else if (p.card.suit === cur.suit && p.card.suit === ledSuit && p.card.rank > cur.rank) {
      winningPlay = p;
    }
    // else: neither trump nor following led suit from a higher position — can't win
  }

  const winner = winningPlay.seat;
  const cards = trick.plays.map(p => p.card);
  const completedTrick: Trick = { ...trick, winner };
  const team = TEAM_OF[winner];

  const capturedCards = {
    A: [...state.capturedCards.A],
    B: [...state.capturedCards.B],
  };
  capturedCards[team].push(...cards);

  const tricks = [...state.tricks, completedTrick];
  const eventLog: GameEvent[] = [...state.eventLog, { type: "trickWon", winner, cards }];

  // Hand complete (6 tricks played)?
  if (tricks.length === 6) {
    return scoreHand({
      ...state,
      tricks,
      currentTrick: undefined,
      capturedCards,
      turn: undefined,
      phase: "scoring",
      eventLog,
    });
  }

  // New trick, winner leads
  return {
    ...state,
    tricks,
    capturedCards,
    currentTrick: { leader: winner, plays: [] },
    turn: winner,
    eventLog,
  };
}

// ============================================================
// Scoring a hand
// ============================================================

export function scoreHand(state: HandState): HandState {
  const trump = state.trump!;
  const bidder = state.bidder!;
  const bid = state.bid!;
  const bidderTeam = TEAM_OF[bidder];

  // To determine High/Low/Jack, we need to look at all trump cards that were dealt
  // (which = all trumps played during the hand, since every card dealt gets played).
  const allPlayedByTeam = state.capturedCards;
  const allTrumps: { card: Card; team: TeamId }[] = [];
  for (const team of ["A", "B"] as TeamId[]) {
    for (const c of allPlayedByTeam[team]) {
      if (c.suit === trump) allTrumps.push({ card: c, team });
    }
  }

  // HIGH: highest trump
  let highTeam: TeamId | undefined;
  if (allTrumps.length > 0) {
    const highest = allTrumps.reduce((a, b) => (a.card.rank >= b.card.rank ? a : b));
    highTeam = highest.team;
  }

  // LOW: lowest trump
  let lowTeam: TeamId | undefined;
  if (allTrumps.length > 0) {
    const lowest = allTrumps.reduce((a, b) => (a.card.rank <= b.card.rank ? a : b));
    lowTeam = lowest.team;
  }

  // JACK: team that captured the Jack of trump (may not be in play)
  let jackTeam: TeamId | undefined;
  const jackInA = state.capturedCards.A.some(c => c.suit === trump && c.rank === 11);
  const jackInB = state.capturedCards.B.some(c => c.suit === trump && c.rank === 11);
  if (jackInA) jackTeam = "A";
  else if (jackInB) jackTeam = "B";

  // GAME: sum of game-points captured. Ties = no point awarded.
  const gpA = state.capturedCards.A.reduce((s, c) => s + GAME_POINTS[c.rank], 0);
  const gpB = state.capturedCards.B.reduce((s, c) => s + GAME_POINTS[c.rank], 0);
  let gameTeam: TeamId | undefined;
  if (gpA > gpB) gameTeam = "A";
  else if (gpB > gpA) gameTeam = "B";

  // Tally points earned
  let earnedA = 0, earnedB = 0;
  for (const t of [highTeam, lowTeam, jackTeam, gameTeam]) {
    if (t === "A") earnedA++;
    else if (t === "B") earnedB++;
  }

  // Set-back rule: if bidder's team didn't meet bid, they LOSE bid points instead.
  const bidderEarned = bidderTeam === "A" ? earnedA : earnedB;
  const bidderMade = bidderEarned >= bid;

  let teamA = earnedA;
  let teamB = earnedB;
  if (!bidderMade) {
    if (bidderTeam === "A") teamA = -bid;
    else teamB = -bid;
  }

  const score: HandScore = {
    teamA,
    teamB,
    high: highTeam,
    low: lowTeam,
    jack: jackTeam,
    game: gameTeam,
    gamePointsA: gpA,
    gamePointsB: gpB,
    bidderMade,
  };

  return {
    ...state,
    score,
    phase: "done",
    eventLog: [...state.eventLog, { type: "score", score }],
  };
}

// ============================================================
// Game-level: running a full 11-point match
// ============================================================

export function applyHandToGame(game: GameState): GameState {
  const hand = game.currentHand;
  if (hand.phase !== "done" || !hand.score) throw new Error("Hand not complete");

  let newScoreA = game.scoreA + hand.score.teamA;
  let newScoreB = game.scoreB + hand.score.teamB;

  const completedHands = [...game.completedHands, hand];
  const ruleset: Ruleset = game.ruleset ?? DEFAULT_RULESET;

  // Abandoned hand (vacation coke) — nobody bid, no scoring, just rotate dealer.
  if (hand.abandoned) {
    const nextDealer = nextSeat(hand.dealer);
    const nextHand = startHand(hand.handNumber + 1, nextDealer, game.seed + hand.handNumber + 1);
    return { ...game, completedHands, currentHand: nextHand };
  }

  const bidderTeam = TEAM_OF[hand.bidder!];

  // Vacation rule: the non-bidding team cannot go out at 11+ on a hand
  // their team didn't bid. Clamp their running total at 10. The bidder team
  // is never clamped — they either earn points or go set normally.
  if (ruleset === "vacation") {
    if (bidderTeam !== "A" && newScoreA > 10) newScoreA = 10;
    if (bidderTeam !== "B" && newScoreB > 10) newScoreB = 10;
  }

  // Win condition: first to 11. If both teams reach 11+ same hand (only
  // possible under TPC, since Vacation clamps the non-bidder at 10), the
  // bidding team wins the tie.
  let winner: TeamId | undefined;
  if (newScoreA >= 11 && newScoreB >= 11) {
    winner = bidderTeam;
  } else if (newScoreA >= 11) {
    winner = "A";
  } else if (newScoreB >= 11) {
    winner = "B";
  }

  if (winner) {
    return {
      ...game,
      scoreA: newScoreA,
      scoreB: newScoreB,
      completedHands,
      winner,
    };
  }

  // Deal next hand. Dealer rotates clockwise.
  const nextDealer = nextSeat(hand.dealer);
  const nextHand = startHand(hand.handNumber + 1, nextDealer, game.seed + hand.handNumber + 1);

  return {
    ...game,
    scoreA: newScoreA,
    scoreB: newScoreB,
    completedHands,
    currentHand: nextHand,
  };
}

/**
 * Running match totals after the current hand's points are applied (same
 * math as the start of `applyHandToGame`, before dealing the next hand).
 * Used in the UI while `phase === "done"` so the scoreboard can show the
 * new totals before the player clicks Deal next hand.
 */
export function projectedRunningTotals(
  priorA: number,
  priorB: number,
  hand: Pick<HandState, "phase" | "score" | "abandoned" | "bidder">,
  ruleset: Ruleset | undefined,
): { scoreA: number; scoreB: number } {
  if (hand.phase !== "done" || !hand.score) {
    return { scoreA: priorA, scoreB: priorB };
  }
  if (hand.abandoned) {
    return { scoreA: priorA, scoreB: priorB };
  }
  let newScoreA = priorA + hand.score.teamA;
  let newScoreB = priorB + hand.score.teamB;
  const rs = ruleset ?? DEFAULT_RULESET;
  if (rs === "vacation" && hand.bidder !== undefined) {
    const bidderTeam = TEAM_OF[hand.bidder];
    if (bidderTeam !== "A" && newScoreA > 10) newScoreA = 10;
    if (bidderTeam !== "B" && newScoreB > 10) newScoreB = 10;
  }
  return { scoreA: newScoreA, scoreB: newScoreB };
}

export function projectedMatchScores(game: GameState): { scoreA: number; scoreB: number } {
  return projectedRunningTotals(game.scoreA, game.scoreB, game.currentHand, game.ruleset);
}

/** Winner implied by current hand result before the next hand is dealt.
 * Returns an existing finalized winner when present.
 */
export function projectedMatchWinner(game: GameState): TeamId | undefined {
  if (game.winner) return game.winner;
  const hand = game.currentHand;
  if (hand.phase !== "done" || !hand.score || hand.abandoned || hand.bidder === undefined) {
    return undefined;
  }
  const { scoreA, scoreB } = projectedMatchScores(game);
  const bidderTeam = TEAM_OF[hand.bidder];
  if (scoreA >= 11 && scoreB >= 11) return bidderTeam;
  if (scoreA >= 11) return "A";
  if (scoreB >= 11) return "B";
  return undefined;
}

/** Scoreboard numbers to show: finalized totals when the match has a winner,
 *  otherwise projected totals including a completed-but-not-booked hand.
 */
export function displayMatchScores(game: GameState): { scoreA: number; scoreB: number } {
  if (game.winner) return { scoreA: game.scoreA, scoreB: game.scoreB };
  return projectedMatchScores(game);
}

/**
 * Solo `HandState.eventLog` and match-level logs both splice hand segments at
 * `{ type: "deal" }`. Keeps multiplayer append-only history from leaking prior
 * hands into "current hand" play lists.
 */
export function eventsSinceLastDeal(events: readonly GameEvent[]): GameEvent[] {
  let lastDeal = -1;
  for (let i = 0; i < events.length; i++) {
    if (events[i]?.type === "deal") lastDeal = i;
  }
  return lastDeal >= 0 ? [...events.slice(lastDeal)] : [...events];
}

/** Cards a seat put down this hand — same six cards they were dealt (full hand). */
export function cardsPlayedFromLogForSeat(
  events: readonly GameEvent[],
  seat: SeatIndex,
): Card[] {
  const slice = eventsSinceLastDeal(events);
  const out: Card[] = [];
  for (const e of slice) {
    if (e.type === "play" && e.seat === seat) out.push(e.card);
  }
  return out;
}

// ============================================================
// Convenience: new game from seed
// ============================================================

export function newGame(
  seed: number,
  players: GameState["players"],
  firstDealer: SeatIndex = 0,
  ruleset: Ruleset = DEFAULT_RULESET,
): GameState {
  return {
    seed,
    ruleset,
    players,
    scoreA: 0,
    scoreB: 0,
    currentHand: startHand(1, firstDealer, seed + 1),
    completedHands: [],
  };
}
